import dayjs from 'dayjs';
import merge from 'lodash/merge';

import {
  ConvertableToDateTime,
  OptionalTimeRange,
  TimeUnit,
} from '@cast/shared-types';
import { timeRange } from '@cast/utils';

type Timestamp = string;
type TimeSeriesEntry<T extends object> = T & { timestamp: Timestamp };

type FillerFn<T extends object> = (
  timestamp: ConvertableToDateTime
) => T & { timestamp: Timestamp };

type FillGapsArgs<VALUE extends TimeSeriesEntry<object>> = {
  timeSeries: VALUE[];
  dateRange: OptionalTimeRange<ConvertableToDateTime>;
  filler: FillerFn<Partial<VALUE>>;
  unit: TimeUnit;
  step?: number;
};

type FillTimeSeriesGapsArgs<VALUE extends TimeSeriesEntry<object>> = {
  timeSeries: VALUE[];
  dateRange: OptionalTimeRange<ConvertableToDateTime>;
  filler: FillerFn<Partial<VALUE>>;
  fillPartial?: boolean;
  unit: TimeUnit;
  step?: number;
};

export const fillGaps = <VALUE extends TimeSeriesEntry<object>>({
  timeSeries,
  dateRange,
  filler,
  unit,
  step = 1,
}: FillGapsArgs<VALUE>): VALUE[] => {
  const from = dayjs(dateRange.from);
  const to = dayjs(dateRange.to);

  const sortedTimeSeries = [...timeSeries].sort((a, b) =>
    dayjs(a.timestamp).isAfter(b.timestamp) ? 1 : -1
  );

  const getKey = (timestamp: ConvertableToDateTime): string =>
    dayjs(timestamp).toISOString();

  if (!sortedTimeSeries.length) {
    return timeRange(from, to, { unit, step }).map(
      (timestamp) => filler(timestamp) as VALUE
    );
  }

  const result: VALUE[] = [];
  const dataByTimestamp = new Map(
    sortedTimeSeries.map((entry) => [getKey(entry.timestamp), entry])
  );

  for (const timestamp of timeRange(from, to, { unit, step })) {
    const key = getKey(timestamp);
    const existing = dataByTimestamp.get(key);

    result.push(existing ?? (filler(timestamp) as VALUE));
  }

  return result;
};

/**
 * Preserves sparse event timestamps while filling the timeline between them.
 * Matches apps/console `utils/metrics.fillGaps` used by the OOM kills chart.
 */
export const fillTimeSeriesGaps = <VALUE extends TimeSeriesEntry<object>>({
  timeSeries,
  dateRange,
  filler,
  fillPartial = false,
  unit,
  step = 1,
}: FillTimeSeriesGapsArgs<VALUE>): VALUE[] => {
  const from = dayjs(dateRange.from);
  let to = dayjs(dateRange.to);

  const sortedTimeSeries = [...timeSeries].sort((a, b) =>
    dayjs(a.timestamp).isAfter(b.timestamp) ? 1 : -1
  );

  const getFiller = (timestamp: ConvertableToDateTime): VALUE =>
    filler(timestamp) as VALUE;

  if (!sortedTimeSeries.length) {
    return timeRange(from, to, { unit, step }).map(getFiller);
  }

  const firstEntry = sortedTimeSeries[0];
  const lastEntry = sortedTimeSeries.at(-1)!;

  const firstTimestamp = dayjs(firstEntry.timestamp);
  const lastTimestamp = dayjs(lastEntry.timestamp);
  if (lastTimestamp.isAfter(to)) {
    to = lastTimestamp;
  }

  const result: VALUE[] = [
    fillPartial ? merge({}, getFiller(firstTimestamp), firstEntry) : firstEntry,
  ];

  let time = firstTimestamp.utc().subtract(step, unit);
  while (!time.isBefore(from)) {
    result.unshift(getFiller(time));
    time = time.subtract(step, unit);
  }

  time = firstTimestamp.utc().add(step, unit);
  let needle = 1;
  while (!time.isAfter(to)) {
    const entry = sortedTimeSeries[needle];
    if (!entry || time.isBefore(entry.timestamp)) {
      result.push(getFiller(time));
    } else {
      result.push(fillPartial ? merge({}, getFiller(time), entry) : entry);
      needle++;
    }
    time = time.add(step, unit);
  }

  return result;
};

export const attachChartTimestampUnix = <T extends { timestamp: string }>(
  entries: T[]
): Array<T & { chartTimestampUnix: number }> =>
  entries.map((entry) => ({
    ...entry,
    chartTimestampUnix: dayjs(entry.timestamp).unix(),
  }));
